import pako from "pako";
import { Buffer } from "buffer";
import { Positions } from "../utils/constants";

/**
 * Moves one or more elements within an array, modifying the original array.
 *
 * @param {Array} array - The array in which elements are to be moved.
 * @param {number} fromIndex - The starting index of the element(s) to move.
 *                             Negative values are treated as offsets from the end of the array.
 * @param {number} toIndex - The target index where the element(s) should be moved.
 *                           Negative values are treated as offsets from the end of the array.
 * @param {number} [count=1] - The number of elements to move. Defaults to 1.
 */
export const arrayMoveMutable = (array, fromIndex, toIndex, count = 1) => {
  const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = toIndex < 0 ? array.length + toIndex : toIndex;

    const item = array.splice(fromIndex, count);
    array.splice(endIndex, 0, ...item);
  }
};

/**
 * Moves one or more elements within an array without modifying the original array.
 * Returns a new array with the changes applied.
 */
export const arrayMoveImmutable = (array, fromIndex, toIndex, count = 1) => {
  array = [...array];
  arrayMoveMutable(array, fromIndex, toIndex, count);
  return array;
};

/**
 * Removes specified elements from an array.
 * @param {Array} arr - The original array.
 * @param {Array} items - The elements to remove.
 * @returns {Array} A new array excluding elements in `items`.
 */
export const removeItems = (arr, items) => {
  return arr.filter((item) => !items.includes(item));
};

/**
 * Calculates the target index for moving an element within an array,
 * considering the desired position relative to the target (before or after)
 * and ensuring the index remains within valid array bounds.
 *
 * @param {Array} array - The array being manipulated.
 * @param {number} sourceIndex - The current index of the element being moved.
 * @param {number} tmpTargetIndex - The initial target index for the move.
 * @param {string} position - The desired position relative to the target index.
 *                             Expected values: `Positions.Before` or `Positions.After`.
 * @returns {number} - The adjusted target index, ensuring it falls within the array's bounds.
 *
 * @example
 * //  Positions = { Before: 'BEFORE', After: 'AFTER' }
 * const arr = [1, 2, 3, 4, 5];
 * const index = calculateTargetIndex(arr, 2, 4, Positions.Before);
 * console.log(index); // Output: 3
 */

export const calculateTargetIndex = (array, sourceIndex, tmpTargetIndex, position) => {
  const maxIndex = array.length;
  const minIndex = 0;

  // Adjust the target index based on the specified position
  const targetIndex = Math.max(
    minIndex,
    Math.min(
      maxIndex,
      tmpTargetIndex + (position === Positions.Before ? -1 : 0) + (sourceIndex < tmpTargetIndex ? 0 : 1)
    )
  );

  return targetIndex;
};
/**
 * Splits an array into smaller arrays (chunks) of a specified size.
 * @param {Array} array - The array to split.
 * @param {number} chunkSize - The size of each chunk.
 * @returns {Array} An array of chunks.
 *
 * * @example
 * chunkArray([1, 2, 3, 4, 5], 2);
 * // Output: [[1, 2], [3, 4], [5]]
 */

export const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

/**
 * Divides an object into smaller objects (chunks) containing a specified number of key-value pairs. *
 * @param {Object} obj - The input object to be divided into chunks.
 * @param {number} chunkSize - The number of key-value pairs in each chunk.
 * @returns {Array} An array of smaller objects (chunks), each containing up to `chunkSize` key-value pairs.
 *
 * @example
 * chunkObject({ a: 1, b: 2, c: 3, d: 4 }, 2);
 * // Output: [{ a: 1, b: 2 }, { c: 3, d: 4 }]
 */
export const chunkObject = (obj, chunkSize) => {
  const keys = Object.keys(obj);
  const chunks = [];

  for (let i = 0; i < keys.length; i += chunkSize) {
    const chunk = {};
    keys.slice(i, i + chunkSize).forEach((key) => {
      chunk[key] = obj[key];
    });
    chunks.push(chunk);
  }

  return chunks;
};
/**
 * Tracks changes in a grid or table-like structure by creating an array of change objects.
 * Each change object represents a change in a specific row and columns with the new values provided.
 * @param {Array} rowIdsInRange  - An array of row identifiers.
 * @param {Array} columnsIdsInRange - An array of column identifiers.
 * @param {Array} newValues - An array of new values corresponding to the rows and columns.
 * @returns {Array} An array of change objects, each containing the rowId, columnIds, and the new value.
 *
 * @example
 * trackChanges([1, 2, 3], ['A', 'B', 'C'], [[10], [20], [30]]);
 * // Output: [
 * //   { rowId: 1, columnIds: ['A', 'B', 'C'], value: 10 },
 * //   { rowId: 2, columnIds: ['A', 'B', 'C'], value: 20 },
 * //   { rowId: 3, columnIds: ['A', 'B', 'C'], value: 30 }
 * // ]
 */

export const trackChanges = (rowIdsInRange, columnsIdsInRange, newValues) => {
  let changes = [];
  rowIdsInRange.forEach((rowId, rowIndex) => {
    changes.push({
      rowId: rowId,
      columnIds: columnsIdsInRange,
      value: newValues.flat().length === 1 ? newValues.flat()[0] : newValues[rowIndex],
    });
  });
  return changes;
};

export const compressData = (data) => {
  const compressed = pako.deflate(JSON.stringify(data));
  return Buffer.from(compressed).toString("base64");
};

/**
 * This function takes an array of numbers as input and returns the index of the maximum value in the array. *
 * @param {Array} array - The input array of numbers..
 * @returns {Array} a number that returns the max number.

 * @example
 * // The maximum value in the array [10, 25, 7, 25, 3] is 25 and the function return 1.
 */
export const getMaxIndex = (array) => {
  return array.reduce(
    (maxIdx, currentValue, currentIndex) => (currentValue > array[maxIdx] ? currentIndex : maxIdx),
    0
  );
};
